/**
 * <s-announcement-rotator> — cycles announcement messages one at a time.
 *
 * - Each direct child (except the pause button) is one message.
 * - A message with data-ends="<ISO date>" is removed once that moment has passed,
 *   so expired promotions stop showing without a theme edit.
 * - A message with data-weekend-only only shows on Saturday and Sunday, Singapore time.
 * - Rotation pauses on hover and keyboard focus, and the pause button stops it entirely.
 * - With one message left, rotation and the pause button are disabled.
 */
class SAnnouncementRotator extends HTMLElement {
  #ac;
  #timer;
  #index = 0;
  #paused = false;
  #hovering = false;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    const now = Date.now();

    const weekendDay = new Intl.DateTimeFormat('en-US', { timeZone: 'Asia/Singapore', weekday: 'short' }).format(new Date(now));
    const isWeekend = weekendDay === 'Sat' || weekendDay === 'Sun';

    for (const item of this.#items()) {
      const ends = Date.parse(item.dataset.ends || '');
      if (!Number.isNaN(ends) && ends < now) item.remove();
      else if (item.hasAttribute('data-weekend-only') && !isWeekend) item.remove();
    }

    const items = this.#items();
    const button = this.querySelector('[data-rotator-toggle]');
    items.forEach((item, i) => { item.hidden = i !== 0; });
    this.dataset.ready = '';

    if (items.length < 2) {
      if (button) button.hidden = true;
      return;
    }

    if (button) {
      button.hidden = false;
      button.addEventListener('click', () => this.#setPaused(!this.#paused, button), opts);
    }

    this.addEventListener('mouseenter', () => { this.#hovering = true; }, opts);
    this.addEventListener('mouseleave', () => { this.#hovering = false; }, opts);
    this.addEventListener('focusin', () => { this.#hovering = true; }, opts);
    this.addEventListener('focusout', () => { this.#hovering = false; }, opts);

    const interval = Number(this.getAttribute('interval')) || 5000;
    this.#timer = setInterval(() => {
      if (this.#paused || this.#hovering) return;
      this.#show(this.#index + 1);
    }, interval);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    clearInterval(this.#timer);
  }

  #items() {
    return [...this.children].filter((el) => !el.hasAttribute('data-rotator-toggle'));
  }

  #show(next) {
    const items = this.#items();
    if (!items.length) return;
    this.#index = next % items.length;
    items.forEach((item, i) => { item.hidden = i !== this.#index; });
  }

  #setPaused(paused, button) {
    this.#paused = paused;
    button.setAttribute('aria-pressed', String(paused));
    button.setAttribute('aria-label', paused ? button.dataset.labelPlay : button.dataset.labelPause);
    button.textContent = paused ? '▶' : '❚❚';
  }
}

if (!customElements.get('s-announcement-rotator')) {
  customElements.define('s-announcement-rotator', SAnnouncementRotator);
}
